<template>
  <chart-section
    :sizeClasses="sizeClasses"
    :loading="loading"
    :error="error"
    :header="header"
    :hasUnapprovedOffers="hasUnapprovedOffers"
    @reloadChart="reloadChart"
    @previewReportClicked="previewReport"
  >
    <template v-slot:body>
      <Bar
        v-if="!loading && !error"
        :id="chartId"
        :options="chartOptions"
        :data="chartData"
      />
      <div v-else />
    </template>
  </chart-section>
</template>

<script lang="js">
import ChartSection from "@/src/components/shared/ChartSection.vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'

// Custon Plugins object
const customHoverPlugin = {
  id: 'customHoverPlugin',

  // this function add a custom hover box on top of the bar chart so we can control the hover colour

  afterDatasetsDraw: (chart, args, opts) => {
    const {ctx, chartArea: {top, bottom}, data, scales: {x, y}} = chart;
    chart.getDatasetMeta(0).data.forEach((metaData, index) => {
      const yHalf = metaData.height / 2;
      if (metaData.active === true) {
        ctx.beginPath();
        ctx.fillStyle = data.datasets[0].hoverBackgroundColor;
        ctx.fillRect(metaData.x, metaData.y - yHalf, -metaData.width, metaData.height);
      }
    })
  }, 

  // You can add all the other hooks here
}
import { Bar } from 'vue-chartjs'


const DEFAULTS = {
  INDEX_AXIS: 'y',
  DATASET_LABEL_NAME: 'Default Label for Dataset in Horizontal Bar Chart',
  SCALE_X_TITLE_TEXT: 'Default Title for X Scale in Horizontal Bar Chart',
  RESULT_LABEL_FIELD: 'organ_name',
  BAR_COLOURS: ['#98D9D9'],
  STACKED: false,
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  customHoverPlugin
)

export default {
  components: {
    ChartSection,
    Bar
  },
  props: {
    chartId: {
      type: String,
      required: true,
    },
    config: {
      type: Object,
      required: false,
      default: () => {},
    },
    results: {
      required: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    error: {
      type: String,
      required: false,
      default: '',
    },
    sizeClasses: {
      type: String,
      required: false,
      default: 'col-md-6 col-sm-12',
    },
    header: {
      type: String,
      required: false,
      default: "Default Header for Horizontal Bar Chart"
    },
    hasUnapprovedOffers: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    chartOptions() {
      const result = {
        indexAxis: DEFAULTS.INDEX_AXIS,
        scales: {
          x: {
            title: {
              display: true,
              text: DEFAULTS.SCALE_X_TITLE_TEXT,
            },
          },
          y: {
            grid: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false
          },
          title:{
            text:"Chart Title",
          },
          customHoverPlugin: [customHoverPlugin]
    },
        responsive: true,
        maintainAspectRatio: false,
        onHover: (event, chartElement) => {
          // changes cursor to a point on hover
          event.native.target.style.cursor = chartElement.length == 1 ? 'pointer' : 'default';
        },
        onClick: (event, elements, chart) => {
          // fires on click
          if (elements[0]) {
            const i = elements[0].index;
            this.$emit("previewReport", {id: chart.canvas.id, label: chart.data.labels[i]})
          }
        }
      };
      const configObject = this.config || {};
      if (configObject.xTitle) result.scales.x.title.text = configObject.xTitle;
      return result;
    },
    chartData() {
      const configObject = this.config || {};
      const resultsArray = this.results || [];
      const datasetConfigs = configObject.datasets || [];
      const resultLabelField = configObject.resultLabelField || DEFAULTS.RESULT_LABEL_FIELD;
      const barColours = configObject.barColours || DEFAULTS.BAR_COLOURS;
      const barHoverColours = configObject.barHoverColours || DEFAULTS.BAR_COLOURS;
      const datasetLabelName = DEFAULTS.DATASET_LABEL_NAME;
      return {
        labels: this.buildLabels(resultsArray, resultLabelField),
        datasets: this.buildDatasets(datasetConfigs, datasetLabelName, resultsArray, barColours, barHoverColours),
      };
    },
  },
  methods: {
    reloadChart() {
      this.$emit("reloadChart");
    },

    previewReport() {
      this.$emit("previewReport");
    },

    buildLabels(resultsArray, resultLabelField) {
      return resultsArray.map((result) => {
        return result[resultLabelField];
      });
    },
    buildDatasets(datasetConfigs, datasetLabelName, resultsArray, barColours, barHoverColours) {
      return datasetConfigs.map((datasetConfig, index) => {
        const dataset = {
          label: datasetLabelName,
          data: [],
        };
        if (datasetConfig.label) dataset.label = datasetConfig.label;
        if (barColours) dataset.backgroundColor = this.buildBarColour(barColours, index);
        if (barHoverColours) dataset.hoverBackgroundColor = this.buildBarColour(barHoverColours, index)
        if (datasetConfig.dataField) {
          dataset.data = resultsArray.map((result) => {
            return result[datasetConfig.dataField];
          });
        }
        return dataset;
      });
    },
    buildBarColour(barColours, index) {
      return barColours[index % barColours.length];
    },
  },
}
</script>
